/**
 * Navigation utilities for `?back=` and `?tab=` URL context.
 *
 * `?back=<internal-path>` is a soft indicator of where the user came from.
 * Used by Cancel buttons, post-save routing, breadcrumb overrides, and
 * Back-button targets on detail pages.
 */
import { useLocation } from "react-router-dom";

// Map of known origin paths to human-readable labels for breadcrumb overrides.
// Keys are compared against the *path* portion of `back` (query stripped).
const BACK_LABELS = {
  "/dispatch": "Dispatch Board",
  "/jobs": "All Jobs",
  "/scheduling/due": "Due Soon",
  "/scheduling/call-to-book": "Call to Book",
  "/scheduling/schedules": "Service Schedules",
  "/customers": "Customers",
  "/sites": "Sites",
  "/machines": "Machines",
  "/machine-types": "Machine Types",
  "/my/jobs": "My Jobs",
  "/reports/engineers": "Engineers Report",
  "/reports/time-on-site": "Time on Site",
  "/reports/actions-outstanding": "Actions Outstanding",
  "/reports/customer-history": "Customer History",
  "/reports/machines-frequency": "Machines Frequency",
};

/**
 * Parse a `?back=` string into a breadcrumb-friendly `{label, to}`.
 * Returns null for missing / external / non-internal values.
 * The label lookup uses the top-level path only — e.g.
 * `/customers/abc123?tab=sites` still resolves to `Customers`.
 */
export function parseBack(back) {
  if (!back || typeof back !== "string") return null;
  if (!back.startsWith("/")) return null; // reject external URLs
  const [pathPart] = back.split("?");
  // Match the longest known-label prefix (e.g. `/customers/xxx` → "Customers").
  let label = BACK_LABELS[pathPart];
  if (!label) {
    // Fallback: use the first two path segments if the full path isn't a known root.
    const parts = pathPart.split("/").filter(Boolean);
    const rootKey = parts.length > 0 ? `/${parts[0]}` : "";
    label = BACK_LABELS[rootKey];
  }
  return { label: label || "Back", to: back };
}

/**
 * Read the `back` query param from a URLSearchParams instance.
 */
export function readBack(searchParams) {
  return parseBack(searchParams.get("back"));
}

/**
 * Append `?back=<encoded>` to a target path.
 * Never nests: if `back` is empty/undefined, returns the target unchanged.
 * Preserves any existing query on the target path with the correct separator.
 */
export function withBack(targetPath, back) {
  if (!back) return targetPath;
  const sep = targetPath.includes("?") ? "&" : "?";
  return `${targetPath}${sep}back=${encodeURIComponent(back)}`;
}

/**
 * Strip query + fragment from a path so two hrefs can be compared by their
 * "entity base" (e.g. `/customers/abc123`).
 */
function _basePath(p) {
  if (!p) return "";
  return p.split("?")[0].split("#")[0];
}

/**
 * For a breadcrumb segment: if `backTo` points to the SAME entity as `naturalTo`
 * (same base path), return `backTo` so clicking the segment returns to the exact
 * prior state (tab + filters preserved). Otherwise return `naturalTo`.
 *
 * This is the rule for Batch 7 follow-up: a breadcrumb crumb overrides to
 * `?back=` only when they share a base path — never over-applies to unrelated
 * segments.
 */
export function segmentBackOverride(naturalTo, backTo) {
  if (!backTo) return naturalTo;
  return _basePath(backTo) === _basePath(naturalTo) ? backTo : naturalTo;
}

/**
 * React hook: returns the current location as a `back=` value (path + search),
 * suitable to be encoded into a drilldown link. Preserves the current page's
 * full state — tabs, filters, search terms — so the destination's Back button
 * / breadcrumb-parent can round-trip back to the exact same state.
 */
export function useBackHref() {
  const loc = useLocation();
  return `${loc.pathname}${loc.search}`;
}

/**
 * React hook: shorthand for building a drilldown `to` URL that carries the
 * current page as `?back=`. Example inside a customer detail's machine row:
 *
 *   const withBackHref = useWithBack();
 *   <Link to={withBackHref(`/machines/${m.id}`)}>…</Link>
 */
export function useWithBack() {
  const back = useBackHref();
  return (target) => withBack(target, back);
}
